import axios, { AxiosError } from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'

import { endpoints } from './endpoints'

import { useDeviceInfosStore } from '@/store/useDeviceInfosStore'
import { useUserAuthSessionStore } from '@/store/useUserAuthSessionStore'
import { eventEmitters } from '@/utils'
import config from '@/config/env'

const { authEventEmitter } = eventEmitters

// API configuration - Always use /api to leverage nginx proxy
const BASE_URL = config.API_BASE_URL

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // Await for 60 seconds
  maxBodyLength: Infinity, // Allowed to send large data
  maxContentLength: Infinity,
  headers: {
    'Content-Type': 'application/json',
  },
})

const refreshAuthLogic = async (failedRequest: AxiosError) => {
  const { refreshToken: currentRefreshToken, initializeSession } =
    useUserAuthSessionStore.getState()

  if (currentRefreshToken) {
    try {
      const { data } = await axios.post<{
        access_token: string
        refresh_token: string
      }>(`${BASE_URL}${endpoints.auth.refreshTokens}`, {
        refresh_token: currentRefreshToken,
      })

      // Update the authorization header for the retried request
      if (failedRequest.response?.config?.headers) {
        failedRequest.response.config.headers['Authorization'] =
          `Bearer ${data.access_token}`
      }

      await initializeSession({
        token: data.access_token,
        refreshToken: data.refresh_token,
      })

      return Promise.resolve()
    } catch (e: any) {
      authEventEmitter.emit('logout')
      return Promise.reject(e)
    }
  }

  return Promise.resolve()
}

api.interceptors.request.use(
  async (config) => {
    const { token: currentToken } = useUserAuthSessionStore.getState()

    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`
    }

    // Get the current device infos updated from the store
    const deviceInfos = useDeviceInfosStore.getState()

    if (config.headers && deviceInfos) {
      // Add custom headers with prefix x-
      config.headers['x-app-version'] = '1.1.3'
      config.headers['x-device-token'] = deviceInfos.device_id
      config.headers['x-phone-model'] = deviceInfos.phone_model ?? ''
      config.headers['x-real-ip-address'] = deviceInfos.real_ip ?? ''
      config.headers['x-platform'] = deviceInfos.platform
      config.headers['x-language'] = deviceInfos.language ?? 'en'
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor for axios-auth-refresh compatibility
//"fix: interceptor rejecting axios error with just error.response instead of the full error object. keep full error object instead"
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Keep the full error object to preserve isAxiosError and other properties
    return Promise.reject(error)
  },
)

createAuthRefreshInterceptor(api, refreshAuthLogic)
